import { createRootRoute, createRoute, createRouter, Outlet } from '@tanstack/react-router';
import React, { Suspense } from 'react';
import ErrorBoundary from './components/ErrorBoundary';
import NotFound from './components/NotFound';
import NetworkError from './components/NetworkError';
import LoadingSpinner from './components/LoadingSpinner';

// Import your components
import Login from './components/Login';
import Signup from './components/Signup';
import Dashboard from './pages/Dashboard';
import About from './pages/About';
import Contact from './pages/Contact';
import Chat from './pages/Chat';
import Navbar from './components/Navbar';

// Protected Route Wrapper Component
const ProtectedComponent = ({ component: Component }) => {
  const auth = localStorage.getItem('user');
  if (!auth) {
    window.location.href = '/login';
    return null;
  }
  return <Component />;
};

// Layout Component with Suspense
const Layout = () => {
  return (
    <ErrorBoundary>
      <div style={{ minHeight: '100vh' }}>
        <Navbar />
        <Suspense fallback={<LoadingSpinner />}>
          <Outlet />
        </Suspense>
      </div>
    </ErrorBoundary>
  );
};

// Root route
const rootRoute = createRootRoute({
  component: Layout,
});

// Define routes with loading states
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: About,
  pendingComponent: () => <LoadingSpinner message="Loading home page..." />,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: Login,
  pendingComponent: () => <LoadingSpinner message="Loading login..." />,
});

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/register',
  component: Signup,
  pendingComponent: () => <LoadingSpinner message="Loading registration..." />,
});

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard',
  component: () => <ProtectedComponent component={Dashboard} />,
  pendingComponent: () => <LoadingSpinner message="Loading dashboard..." />,
});

const chatRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/chat',
  component: () => <ProtectedComponent component={Chat} />,
  pendingComponent: () => <LoadingSpinner message="Loading chat..." />,
});

const aboutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/about',
  component: About,
  pendingComponent: () => <LoadingSpinner message="Loading about page..." />,
});

const contactRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/contact',
  component: Contact,
  pendingComponent: () => <LoadingSpinner message="Loading contact page..." />,
});

// Catch-all route for 404
const notFoundRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '*',
  component: NotFound,
});

// Create the route tree
const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  registerRoute,
  dashboardRoute,
  chatRoute,
  aboutRoute,
  contactRoute,
  notFoundRoute,
]);

// Create the router instance
export const router = createRouter({
  routeTree,
  defaultErrorComponent: ({ error }) => {
    // Handle network/API errors
    if (error?.response || error?.message?.includes('Network Error')) {
      return <NetworkError error={error} retry={() => window.location.reload()} />;
    }

    // Handle other errors
    return (
      <ErrorBoundary>
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h1>Oops! Something went wrong.</h1>
          <p>{error.message}</p>
          <button onClick={() => window.location.reload()}>Refresh Page</button>
        </div>
      </ErrorBoundary>
    );
  },
});
